import styled from '@emotion/styled'
import { Link } from 'react-router-dom'
import { Icon } from '../../shared/ui/Icon'
import { colors } from '../../shared/ui/colors'
import { Skeleton } from '../../shared/ui/Skeleton'

export const TableContainer = styled.div`
  border: 1px solid ${colors.gray200};
  border-radius: 16px;
  overflow: auto hidden;
  background: ${colors.white};
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.06);
  max-width: 100%;
`

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`

export const TableHeader = styled.thead`
  background: ${colors.gray50};
`

export const HeaderCell = styled.th<{ align?: 'left' | 'right' | 'center' }>`
  padding: 15px 7px;
  text-align: ${({ align = 'right' }) => align};
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  color: ${colors.gray900};
  border-bottom: 1px solid ${colors.gray200};
  border-right: 1px solid ${colors.gray200};

  &:first-of-type {
    padding-left: 16px;
    border-left: 1px solid ${colors.gray200};
  }

  &:last-of-type {
    padding-right: 16px;
    border-right: 1px solid ${colors.gray200};
  }
`

export const BodyRow = styled.tr<{ accent?: string }>`
  background: ${({ accent }) => accent || colors.white};
  border-bottom: 1px solid ${colors.gray200};
  transition: background 0.2s ease;

  &:last-of-type {
    border-bottom: none;
  }

  &:hover {
    background: ${({ accent }) => accent || colors.gray100};
  }
`

export const TitleCell = styled.td<{ level: number }>`
  padding: 10px 7px;
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.gray900};
  border-right: 1px solid ${colors.gray200};
  min-width: 420px;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    min-width: 320px;
  }
`

export const MetricsCell = styled.td<{ align?: 'left' | 'right' | 'center' }>`
  padding: 10px 7px;
  text-align: ${({ align = 'right' }) => align};
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  color: ${colors.gray800};
  border-right: 1px solid ${colors.gray200};
  white-space: nowrap;
  min-width: 120px;
  max-width: 120px;

  &:last-of-type {
    border-right: 1px solid ${colors.gray200};
    padding-right: 16px;
  }
`

export const EstimatesLink = styled(Link)`
  display: block;
`

export const ToggleButton = styled.button<{ disabled?: boolean }>`
  display: ${({ disabled }) => (disabled ? 'none' : 'inline-flex')};
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  transition:
    background 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;

  &:hover {
    background: ${colors.gray100};
    box-shadow: 0 0 0 1px ${colors.gray300};
  }

  &:focus-visible {
    outline: 2px solid ${colors.orange500};
    outline-offset: 2px;
  }
`

export const ChevronIcon = styled(Icon)<{ isExpanded: boolean }>`
  transition: transform 0.2s ease;
  transform: rotate(${({ isExpanded }) => (isExpanded ? '0deg' : '-90deg')});
`

export const LeafPlaceholder = styled.span`
  display: inline-flex;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`

export const Indent = styled.span<{ level: number }>`
  display: inline-flex;
  width: ${({ level }) => level * 18}px;
  flex-shrink: 0;
`

export const ItemCode = styled.span`
  font-weight: 600;
  color: ${colors.gray700};
  font-variant-numeric: tabular-nums;
`

export const ItemName = styled.span`
  color: ${colors.gray900};
`

export const TableSkeletonWrapper = styled(TableContainer)`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const TableSkeletonRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: nowrap;
  min-width: max-content;
`

export const TableSkeletonLine = styled(Skeleton)`
  flex: 1 0 420px;

  &:not(:first-of-type) {
    flex-basis: 160px;
  }
`
